/**
 * Application Configuration
 * Centralized settings for the entire application
 * This file contains global configuration values that affect the behavior and appearance of the app
 * 
 * Note: Some values can be overridden by ApplicationSettings from the database.
 * Use the SettingsContext to access merged configuration values.
 */

import { localDateKey } from '../utils/timeZone';
import {
  DISPLAY_TIME_LOCALE,
  formatDisplayDate,
  formatDisplayTime,
  resolveUserDateFormat,
  formatDateWithPattern,
} from '../utils/userDatePreferences';

export const APP_CONFIG = {
  // ============================================================================
  // DATE & TIME FORMATTING
  // ============================================================================
  
  /**
   * Date format used throughout the application
   * Format: DD/MM/YYYY
   */
  dateFormat: 'DD/MM/YYYY',
  
  /**
   * Date-time format for detailed timestamps
   * Format: DD/MM/YYYY HH:mm
   */
  dateTimeFormat: 'DD/MM/YYYY HH:mm',
  
  /**
   * Time format for time-only displays
   * Format: HH:mm
   */
  timeFormat: 'HH:mm',
  
  /**
   * Date format for API requests/responses (ISO format)
   * Format: YYYY-MM-DD
   */
  apiDateFormat: 'YYYY-MM-DD',
  
  /**
   * DateTime format for API requests/responses
   * Format: ISO 8601
   */
  apiDateTimeFormat: 'YYYY-MM-DDTHH:mm:ss.SSSZ',

  // ============================================================================
  // TABLE CONFIGURATION
  // ============================================================================
  
  table: {
    /**
     * Default page size for tables
     */
    defaultPageSize: 200,
    
    /**
     * Available page size options
     */
    pageSizeOptions: [50, 100, 200, 500],
    
    /**
     * Default table density
     */
    defaultDensity: 'normal' as const, // 'compact', 'normal', 'comfortable'
    
    /**
     * Table density configurations
     */
    densityConfig: {
      compact: {
        description: 'Tight spacing, more rows visible',
        rowHeight: 'h-8',
        cellPadding: 'px-3 py-1.5',
        headerPadding: 'px-3 py-2',
      },
      normal: {
        description: 'Standard spacing, balanced view',
        rowHeight: 'h-10',
        cellPadding: 'px-4 py-2',
        headerPadding: 'px-4 py-3',
      },
      comfortable: {
        description: 'Loose spacing, easier to read',
        rowHeight: 'h-12',
        cellPadding: 'px-6 py-3',
        headerPadding: 'px-6 py-4',
      },
    },
    
    /**
     * Available density options
     */
    densityOptions: ['compact', 'normal', 'comfortable'] as const,
    
    /**
     * Default date column width
     */
    dateColumnWidth: '120px',
    
    /**
     * Default datetime column width
     */
    dateTimeColumnWidth: '150px',
  },

  // ============================================================================
  // API SETTINGS
  // ============================================================================
  
  api: {
    /**
     * Request timeout (in milliseconds)
     * Can be overridden by ApplicationSettings.apiTimeout
     *
     * Lowered from 30000 to 10000 to avoid the UI
     * feeling frozen for a long time when the backend
     * is unreachable or very slow.
     */
    timeout: 10000,
    
    /**
     * Retry attempts for failed requests
     * Can be overridden by ApplicationSettings.apiRetryAttempts
     */
    retryAttempts: 3,
    
    /**
     * Retry delay (in milliseconds)
     * Can be overridden by ApplicationSettings.apiRetryDelay
     */
    retryDelay: 1000,

    /** Folder template import: upload + server parse/save per batch (15 min). */
    folderImportTimeout: 900_000,

    /** Email list queries can be slower than lightweight API calls. */
    emailListTimeout: 30000,

    /** Plain send / reply / forward without large payloads. Overridden by ApplicationSettings.emailSendTimeout. */
    emailSendTimeout: 60_000,

    /** Send with attachments. Overridden by ApplicationSettings.emailSendWithAttachmentsTimeout. */
    emailSendWithAttachmentsTimeout: 300_000,

    /**
     * How Compose transfers attachments.
     * Overridden by ApplicationSettings.emailAttachmentTransferMode.
     */
    emailAttachmentTransferMode: 'json_base64' as 'json_base64' | 'multipart',

    /** Max total Compose attachment size (MB). Overridden by ApplicationSettings.emailAttachmentMaxSizeMb. */
    emailAttachmentMaxSizeMb: 25,

    /** Local Ollama / LLM calls (pre-call brief, smart replies, etc.). */
    aiAssistantTimeout: 120_000,
    
    /**
     * SWR cache duration (in milliseconds)
     * Can be overridden by ApplicationSettings.apiCacheDuration
     */
    cacheDuration: 300000, // 5 minutes
  },

  // ============================================================================
  // FEATURE FLAGS
  // ============================================================================
  
  features: {
    /**
     * Enable advanced search functionality
     * Can be overridden by ApplicationSettings.enableAdvancedSearch
     */
    enableAdvancedSearch: true,
    
    /**
     * Enable export functionality
     * Can be overridden by ApplicationSettings.enableExport
     */
    enableExport: true,
    
    /**
     * Enable bulk operations
     * Can be overridden by ApplicationSettings.enableBulkOperations
     */
    enableBulkOperations: true,
    
    /**
     * Enable table presets
     * Can be overridden by ApplicationSettings.enableTablePresets
     */
    enableTablePresets: true,
    
    /**
     * Show application footer
     * Can be overridden by ApplicationSettings.enableFooter
     */
    enableFooter: true,
    
    /**
     * Enable audit logging
     * Can be overridden by ApplicationSettings.enableAuditLogging
     */
    enableAuditLogging: true,

    /**
     * Enable Send-like encrypted file links (upload UI + Compose hook).
     */
    fileSend: true,
  },

  // ============================================================================
  // DASHBOARD GRID (default widget layout)
  // ============================================================================
  /** Per row = gridColumns ÷ width. Default: 6 stat, 3 list, 2 chart per row */
  dashboard: {
    gridColumns: 6,
    statCardWidth: 1,
    listWidgetWidth: 2,
    chartWidth: 3,
  },

  // ============================================================================
  // LAYOUT
  // ============================================================================
  layout: {
    /** Primary navigation chrome style (classic sidebar). */
    sidebarStyle: 'classic' as const,
  },

  // ============================================================================
  // SYSTEM SETTINGS
  // ============================================================================
  
  system: {
    /**
     * Application name
     * Can be overridden by ApplicationSettings.appName
     */
    appName: 'u-comm',
    
    /**
     * Application version
     */
    version: '1.0.0',
    
    /**
     * Environment
     */
    environment: process.env.NODE_ENV || 'development',
    
    /**
     * Debug mode
     */
    debugMode: process.env.NODE_ENV === 'development',
    
    /**
     * Session timeout (in milliseconds)
     * Can be overridden by ApplicationSettings.sessionTimeout
     */
    sessionTimeout: 8 * 60 * 60 * 1000, // 8 hours
  },
};

/**
 * Format a date string or Date object to DD/MM/YYYY format
 * @param date - Date string, Date object, or null/undefined
 * @returns Formatted date string in DD/MM/YYYY format, or empty string if invalid
 */
export const formatDate = (
  date: string | Date | null | undefined,
  options?: FormatDateTimeOptions,
): string => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(dateObj.getTime())) {
      return '';
    }

    return formatDisplayDate(dateObj, options);
  } catch {
    return '';
  }
};

/**
 * Format a date for filter comparison (locale-independent).
 * Uses UTC and fixed DD/MM/YYYY so filtering works identically on Windows and Linux/Docker
 * where toLocaleDateString can differ or lack en-GB support.
 * Use this for date filter getValue and applyFilter, not for general display.
 */
export const formatDateForFilter = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';

    return formatDateWithPattern(dateObj, resolveUserDateFormat(), 'UTC');
  } catch {
    return '';
  }
};

export interface FormatDateTimeOptions {
  timeZone?: string;
  dateFormat?: string;
  timeFormat?: string;
}

/**
 * Format a date string or Date object to DD/MM/YYYY HH:mm format
 * @param date - Date string, Date object, or null/undefined
 * @returns Formatted date-time string in DD/MM/YYYY HH:mm format, or empty string if invalid
 */
export const formatDateTime = (
  date: string | Date | null | undefined,
  options?: FormatDateTimeOptions,
): string => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return '';
    }

    const dateStr = formatDisplayDate(dateObj, options);
    const timeStr = formatDisplayTime(dateObj, options);
    
    return `${dateStr} ${timeStr}`;
  } catch {
    return '';
  }
};

/**
 * Format a date for the email list: if the date is today, show only time; otherwise show full date-time.
 * @param date - Date string, Date object, or null/undefined
 * @returns Time only (e.g. "14:30") when today, otherwise DD/MM/YYYY HH:mm
 */
export const formatDateTimeForList = (
  date: string | Date | null | undefined,
  options?: FormatDateTimeOptions,
): string => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(dateObj.getTime())) {
      return '';
    }

    const isToday =
      localDateKey(dateObj, options?.timeZone) === localDateKey(new Date(), options?.timeZone);

    if (isToday) {
      return formatDisplayTime(dateObj, options);
    }

    return formatDateTime(dateObj, options);
  } catch {
    return '';
  }
};

/**
 * Format a date string or Date object to time only (HH:mm format)
 * @param date - Date string, Date object, or null/undefined
 * @returns Formatted time string in HH:mm format, or empty string if invalid
 */
export const formatTime = (
  date: string | Date | null | undefined,
  options?: FormatDateTimeOptions,
): string => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(dateObj.getTime())) {
      return '';
    }

    return formatDisplayTime(dateObj, options);
  } catch {
    return '';
  }
};

/**
 * Short date for dashboards and compact UI (e.g. "15 Jan 2026").
 */
export const formatDateShort = (
  date: string | Date | null | undefined,
  options?: FormatDateTimeOptions,
): string => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';

    const tzOpts = options?.timeZone ? { timeZone: options.timeZone } : {};
    return dateObj.toLocaleDateString(DISPLAY_TIME_LOCALE, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...tzOpts,
    });
  } catch {
    return '';
  }
};

/**
 * Date-time string using the user's date format preference.
 */
export const formatLocaleDateTime = (
  date: string | Date | null | undefined,
  options?: FormatDateTimeOptions,
): string => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';

    return `${formatDisplayDate(dateObj, options)} ${formatDisplayTime(dateObj, options)}`;
  } catch {
    return '';
  }
};

/**
 * Date with custom Intl.DateTimeFormat options (fixed en-GB locale).
 */
export const formatDateLocalized = (
  date: string | Date | null | undefined,
  intlOptions: Intl.DateTimeFormatOptions,
  options?: FormatDateTimeOptions,
): string => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';

    const tzOpts = options?.timeZone ? { timeZone: options.timeZone } : {};
    return dateObj.toLocaleDateString(DISPLAY_TIME_LOCALE, { ...intlOptions, ...tzOpts });
  } catch {
    return '';
  }
};

/**
 * Format a date for API requests (YYYY-MM-DD format)
 * @param date - Date string, Date object, or null/undefined
 * @returns Formatted date string in YYYY-MM-DD format, or empty string if invalid
 */
export const formatDateForAPI = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    
    return dateObj.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

/**
 * Get merged application configuration
 * This function merges APP_CONFIG defaults with ApplicationSettings from the database.
 * 
 * @deprecated Use SettingsContext (useSettings hook) instead for runtime access to merged config
 * This function is kept for backward compatibility and static access
 */
export const getMergedAppConfig = () => {
  return APP_CONFIG;
};
